import React, { ChangeEvent, useId, useState } from 'react';
import { MediumHeader } from '../../ui/Header/Header';
import Button from '../../ui/Button/BlockButton';
import TextArea from '../../ui/TextArea/TextArea';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import StarRating from './StarRating/StarRating';
import { isSubmitDisabled } from './lib/submitGate';
import { useTheme } from '../../../hooks/useTheme';
import { useSettings } from '../../../hooks/useSettings';
import { useTranslations } from '../../../hooks/useTranslations';
import styles from './Portrait.module.css';

export interface PortraitProps {
  featureName?: string;
  selectedRating: number;
  onRatingSelect: (index: number) => void;
  onClickSupportLink?: () => void;
  submitFeedback?: (feedbackComment: string) => void;
  commentOptional?: boolean;
  collapsible?: boolean;
}

const Portrait = ({
  featureName,
  selectedRating,
  onRatingSelect,
  onClickSupportLink,
  submitFeedback,
  commentOptional,
  collapsible = false,
}: PortraitProps) => {
  const {
    feedbackButtonText = '',
    feedbackCommentLabel = '',
    feedbackCustomerSupportLink = '',
    feedbackExperienceHeader = '',
  } = useTranslations();

  const {
    linksNoneDefault: textLinkStyle,
    linksNoneHover: textLinkHoverStyle,
  } = useTheme();

  const { feedback } = useSettings();

  const { customerSupportLink } = feedback || {};

  const [feedbackComment, setFeedbackComment] = useState('');
  const [collapsed, setCollapsed] = useState(collapsible);
  const commentId = useId();

  return (
    <div data-test="feedbackPortraitView">
      <div className={styles.portraitHeader}>
        {feedbackExperienceHeader && (
          <MediumHeader as="p">{feedbackExperienceHeader}</MediumHeader>
        )}
      </div>
      <StarRating
        onClick={(_event, index) => {
          onRatingSelect(index);
          setCollapsed(false);
        }}
        selectedRating={selectedRating}
        featureName={featureName}
      />
      {!collapsed && (
        <div data-test="feedbackPortraitExpandedBlock">
          <div className={styles.feedbackBox}>
            <TextArea
              id={commentId}
              animateLabel
              label={feedbackCommentLabel || ''}
              featureName={featureName}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                setFeedbackComment(event.target.value)
              }
            />
          </div>
          <div className={styles.feedbackAction}>
            <Button
              disabled={isSubmitDisabled(
                selectedRating,
                commentOptional,
                feedbackComment,
              )}
              big
              onClick={() => {
                if (submitFeedback) submitFeedback(feedbackComment);
              }}
            >
              {feedbackButtonText}
            </Button>
          </div>
          {customerSupportLink && (
            <TextLink
              target={TextLinkTargetType.BLANK}
              href={customerSupportLink}
              linkStyle={{
                sDefault: textLinkStyle,
                sHover: textLinkHoverStyle,
              }}
              onClick={() => {
                if (onClickSupportLink) onClickSupportLink();
              }}
            >
              {feedbackCustomerSupportLink}
            </TextLink>
          )}
        </div>
      )}
    </div>
  );
};

export default Portrait;
